/**
 * Focus-lock target selection. See CLAUDE.md > Combat & Focus.
 *
 * F     → lock onto the nearest visible entity, or unlock if already locked
 * G / V → cycle focus left / right through the visible set
 *
 * Headless — no PixiJS.
 */

import { CONFIG } from '../config';
import { computeVisionCone } from './fov';
import type { Guard } from './guard';
import { tileAt, type MapData } from './map';
import type { Player } from './player';
import type { Targetable } from './targetable';
import { coverShadowMargin, hasCoverBetween } from './cover';
import { isUnlit } from './light';
import { blocksVision, blocksVisionCrouched } from './tiles';

/** Recompute the player's cone FOV. Written to `player.fovTiles`. */
export function syncPlayerFov(
  player: Player,
  map: MapData,
  /** Extra sight-blockers that aren't tiles — steam blinds you too. */
  fog?: (x: number, y: number) => boolean,
): void {
  const fov = new Set<number>();
  const crouched = player.stance === 'crouch';
  const range = CONFIG.vision.range * (crouched ? CONFIG.vision.crouchRangeMult : 1);
  const halfAngle = CONFIG.vision.halfAngleRad * (crouched ? CONFIG.vision.crouchAngleMult : 1);
  computeVisionCone(
    player.x,
    player.y,
    player.facingDx,
    player.facingDy,
    range,
    halfAngle,
    // Only walls + Void are hard-opaque. Cover (cubicle partitions, half walls)
    // is opaque only to a crouched observer; a standing one sees over it (the
    // one-tile foot shadow is applied afterwards via coverShadowMargin).
    (x, y) => {
      if (fog?.(x, y) === true) return true;
      const t = tileAt(map, x, y);
      return crouched ? blocksVisionCrouched(t) : blocksVision(t);
    },
    (x, y) => fov.add(y * map.width + x),
  );
  // Standing: cover casts a one-tile shadow directly behind it (see into the
  // room, but not the tile hugging the back of the wall). Crouched observers
  // already treat cover as opaque, so there's nothing to trim.
  if (!crouched) coverShadowMargin(map, player.x, player.y, fov);
  player.fovTiles = fov;
}

/**
 * True when `guard` is concealed from the player by crouching behind cover —
 * the player→guard mirror of `guardHasLosToPlayer`. A crouched guard with a
 * cubicle partition / half wall on the sightline can't be seen (it's ducked
 * below the cover), so guard cover is symmetric.
 */
export function guardConcealedByCover(player: Player, guard: Targetable, map: MapData): boolean {
  return guard.stance === 'crouch' && hasCoverBetween(map, player.x, player.y, guard.x, guard.y);
}

/** Whether the player currently sees `guard`: alive, its tile in the player's
 *  FOV, and not concealed by crouching behind cover. */
export function playerSeesGuard(
  player: Player,
  guard: Targetable,
  fov: Set<number>,
  map: MapData,
): boolean {
  if (guard.body.dead) return false;
  if (!fov.has(guard.y * map.width + guard.x)) return false;
  return !guardConcealedByCover(player, guard, map);
}

/**
 * Recompute, per guard, `hiddenByCover` and `perceivedByPlayer` — the set the
 * player can focus/aim and keep focus on. A guard is PERCEIVED when it is:
 *   - in the view cone (`fovTiles`) and not crouched behind cover, OR
 *   - currently audible (heard — `hearingUntil` still in the future), OR
 *   - marked (deliberately tracked) and seen at least once.
 * Runs each tick after the player's FOV is finalized, before focus resolution.
 * `ticks` is the current world tick (for the audible window).
 */
export function updatePlayerPerception(
  player: Player,
  guards: readonly Targetable[],
  map: MapData,
  ticks: number,
): void {
  const fov = player.fovTiles;
  for (const g of guards) {
    const concealed = guardConcealedByCover(player, g, map);
    g.hiddenByCover = concealed;
    if (g.body.dead) {
      g.perceivedByPlayer = false;
      continue;
    }
    // Close-proximity "6th sense": in focus mode you sense (and can attack) a
    // guard within `proximityTiles` even in pitch darkness.
    const cheb = Math.max(Math.abs(g.x - player.x), Math.abs(g.y - player.y));
    const senseProximity = player.focusActive && cheb <= CONFIG.vision.proximityTiles;
    // A guard standing in the dark can't otherwise be seen or focused — even if
    // marked or heard, you have no shot on a target you cannot see. Night vision
    // lifts that: with NV on, a lit-enough-under-NV guard in your cone is fair game.
    if (isUnlit(map, g.x, g.y) && !senseProximity && !player.nightVisionOn) {
      g.perceivedByPlayer = false;
      continue;
    }
    const inSight = !!fov && fov.has(g.y * map.width + g.x) && !concealed;
    const audible = (player.hearingUntil.get(g.id) ?? -1) > ticks;
    const marked = g.marked && g.everSeenByPlayer;
    g.perceivedByPlayer = inSight || audible || marked || senseProximity;
  }
}

/** Guards the player can currently focus/aim — everything it perceives (see
 *  `updatePlayerPerception`). `map` is kept for signature stability. */
export function visibleTargets(
  _player: Player,
  guards: readonly Targetable[],
  _map: MapData,
): Targetable[] {
  return guards.filter((g) => g.perceivedByPlayer && !g.body.dead);
}

/**
 * React to being hit WITHOUT forcing focus. Instead of auto-locking, this raises
 * a directional "F Focus!" prompt (the world-space direction toward the
 * attacker) so the player can CHOOSE to lock on and fight back — or ignore it and
 * run. The attacker is registered as heard (`hearingUntil`) — it revealed itself
 * by firing — so if the player does press F, the lock can find it. `ticks` is the
 * current world tick.
 */
export function noteIncomingAttack(player: Player, attacker: Guard, ticks: number): void {
  if (player.body.dead || attacker.body.dead) return;
  player.hearingUntil.set(attacker.id, ticks + CONFIG.sound.fadeAgeTicks);
  const dx = attacker.x - player.x;
  const dy = attacker.y - player.y;
  const mag = Math.hypot(dx, dy) || 1;
  player.hitDirX = dx / mag;
  player.hitDirY = dy / mag;
  player.focusPromptTimer = CONFIG.hud.focusPromptSec;
}

/**
 * How much this target wants your attention, before distance. A guard levelling
 * a pistol matters more than one who hasn't noticed you, who matters more than
 * the worker at the photocopier — and a lock is scenery until you decide
 * otherwise.
 *
 * Read off the shape rather than the class: `Targetable` is all focus knows, so
 * anything with an awareness state ranks by it and everything else falls to the
 * bystander tier. That's what makes any FUTURE live entity focusable without
 * touching this function.
 */
export function targetThreat(t: Targetable): number {
  const state = (t as { state?: string }).state;
  switch (state) {
    case 'alert': return 10;
    case 'cautious': return 6;
    case 'suspicious': return 3;
    case 'unaware': return 2;   // an armed guard who hasn't seen you yet
    default: break;
  }
  // No awareness ladder at all: a lock. Focusable, but never the automatic pick.
  return (t as { body?: unknown }).body !== undefined && state === undefined ? 0.5 : 1;
}

/**
 * Pick what to lock: threat weighted against distance. A guard across the room
 * outranks an oblivious civilian at your elbow, but the civilian you're standing
 * next to still beats a guard two rooms away.
 */
function bestTarget(player: Player, targets: readonly Targetable[]): Targetable | null {
  let best: Targetable | null = null;
  let bestScore = -Infinity;
  for (const g of targets) {
    const score = focusScore(player, g);
    if (score > bestScore) {
      bestScore = score;
      best = g;
    }
  }
  return best;
}

/** Threat over distance — exported so the cycle order matches the auto-pick. */
export function focusScore(player: Player, t: Targetable): number {
  const dx = t.x - player.x;
  const dy = t.y - player.y;
  const dist = Math.hypot(dx, dy);
  return targetThreat(t) / (1 + dist * CONFIG.focus.distanceWeight);
}

/**
 * Toggle focus MODE. Turning it ON immediately locks the nearest perceived
 * target, or — if none is perceivable — leaves you PRIMED (focusActive with no
 * lock), so focus mode keeps watching for a target to appear. Turning it OFF
 * clears everything.
 */
export function toggleFocus(player: Player, guards: readonly Targetable[], map: MapData): void {
  if (player.focusActive) {
    player.focusActive = false;
    player.focusedTargetId = null;
    return;
  }
  player.focusActive = true;
  const t = bestTarget(player, visibleTargets(player, guards, map));
  player.focusedTargetId = t?.id ?? null;
}

/** Cycle focus among currently-visible targets. dir = +1 next, -1 previous. */
export function cycleFocus(
  player: Player,
  guards: readonly Targetable[],
  map: MapData,
  dir: 1 | -1,
): void {
  const targets = visibleTargets(player, guards, map);
  if (targets.length === 0) {
    player.focusedTargetId = null;
    return;
  }
  // Same ranking the auto-pick uses, so cycling walks targets in the order the
  // game considers them important (id breaks ties so it stays deterministic).
  const sorted = [...targets].sort((a, b) => focusScore(player, b) - focusScore(player, a) || a.id - b.id);
  const idx = sorted.findIndex((g) => g.id === player.focusedTargetId);
  if (idx < 0) {
    player.focusedTargetId = sorted[0]!.id;
    return;
  }
  const next = (idx + dir + sorted.length) % sorted.length;
  player.focusedTargetId = sorted[next]!.id;
}

/**
 * Maintain focus mode each tick (run after `updatePlayerPerception`).
 *  - Focus mode OFF → no lock.
 *  - Current lock still alive + perceived → keep it.
 *  - Otherwise (killed / lost / none yet / just primed) → re-acquire the nearest
 *    perceived target. If nothing is perceivable, stay PRIMED (no lock) and keep
 *    watching — dropping one enemy flows straight into the next, and priming
 *    with no targets simply waits for one to appear.
 */
export function refreshFocus(player: Player, guards: readonly Targetable[], map: MapData): void {
  if (!player.focusActive) {
    player.focusedTargetId = null;
    return;
  }
  const target = player.focusedTargetId !== null
    ? guards.find((g) => g.id === player.focusedTargetId)
    : null;
  if (target && !target.body.dead && target.perceivedByPlayer) return; // lock still good
  const next = bestTarget(player, visibleTargets(player, guards, map));
  const nextId = next?.id ?? null;
  if (nextId !== player.focusedTargetId) {
    player.focusedTargetId = nextId;
    // Target changed (new lock, or dropped to primed) → restart limb selection.
    player.selectedLimbIdx = -1;
  }
}
